import type React from 'react';

import { cn } from '@skrym/shared/utils/cn';

import { getOrganizationAssets } from '../utils/organizations';
import { OrganizationIcon } from './organization-icon';

interface NodeIconProps extends React.ComponentProps<'div'> {
  name: string;
  onClick?: () => void;
  size: number;
  gradient?: boolean;
  overrideBgColor?: string; // always applied
  overrideIconColor?: string; // always applied
  fallbackBgColor?: string; // only applied if organization asset is not found
  fallbackIconColor?: string; // only applied if organization asset is not found
  className?: string;
}

function NodeIcon(props: NodeIconProps) {
  const {
    name,
    onClick,
    size,
    overrideBgColor,
    overrideIconColor,
    gradient = false,
    className,
    ...rest
  } = props;
  // TODO: Add support for passing icons, and colors explicitly? Needs to support e.g. checkout strategy
  // Find the organization asset matching the name or ID
  const organizationAsset = getOrganizationAssets(name);

  // Default colors if organization asset is not found
  let backgroundColor = '#666666';
  let iconColor = '#000000';

  if (organizationAsset) {
    backgroundColor = organizationAsset.lightColors?.background || backgroundColor;
    iconColor = organizationAsset.lightColors?.logo || iconColor;
  } else if (name.toLowerCase() === 'skrym') {
    // Special case for 'skrym'
    backgroundColor = '#f8ea21';
    iconColor = '#000000';
  }

  // apply fallback colors if we don't have any colors from org assets
  if (
    (!organizationAsset || organizationAsset.lightColors?.background === undefined) &&
    props.fallbackBgColor
  ) {
    backgroundColor = props.fallbackBgColor;
  }

  if (
    (!organizationAsset || organizationAsset.lightColors?.logo === undefined) &&
    props.fallbackIconColor
  ) {
    iconColor = props.fallbackIconColor;
  }

  // Override colors if provided
  if (overrideBgColor !== undefined) {
    backgroundColor = overrideBgColor;
  }
  if (overrideIconColor !== undefined) {
    iconColor = overrideIconColor;
  }

  // Function to lighten/darken color
  const lightenDarkenColor = (_col: string, amt: number) => {
    let usePound = false;

    let col = _col;
    if (col[0] === '#') {
      col = col.slice(1);
      usePound = true;
    }

    const num = Number.parseInt(col, 16);

    let r = (num >> 16) + amt;

    if (r > 255) r = 255;
    else if (r < 0) r = 0;

    let g = ((num >> 8) & 0x00ff) + amt;

    if (g > 255) g = 255;
    else if (g < 0) g = 0;

    let b = (num & 0x0000ff) + amt;

    if (b > 255) b = 255;
    else if (b < 0) b = 0;

    const newColor = (r << 16) | (g << 8) | b;
    return (usePound ? '#' : '') + newColor.toString(16).padStart(6, '0');
  };

  const gradientStart = lightenDarkenColor(backgroundColor, 20); // Lighten by 20
  const gradientEnd = lightenDarkenColor(backgroundColor, -20); // Darken by 20

  const containerStyle: React.CSSProperties = {
    borderRadius: `${0.25 * size}px`,
    width: `${size}px`,
    height: `${size}px`,
    backgroundColor: backgroundColor,
  };

  const gradientStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(to right, ${gradientStart}, ${gradientEnd})`,
  };

  if (gradient) {
    Object.assign(containerStyle, gradientStyle);
  }

  const innerStyle: React.CSSProperties = {
    maxWidth: `${size}px`,
    maxHeight: `${size}px`,
    padding: `${0.12 * size}px`,
    color: iconColor,
    lineHeight: `${size}px`, // Center the icon vertically - otherwise becomes weird in tables.
  };

  const containerClasses = cn(
    'flex items-center',
    onClick
      ? 'cursor-pointer transition-shadow duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1'
      : '',
    className,
  );

  const innerClasses = cn('flex items-center justify-center');

  const content = (
    <div className={innerClasses} style={innerStyle}>
      <OrganizationIcon organization={name} width="100%" height="100%" />
    </div>
  );

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: when touching this code, please fix this lint issue or change this comment to explain why it should be kept
    <div
      className={containerClasses}
      style={containerStyle}
      onClick={onClick}
      onKeyDown={onClick}
      {...rest}
    >
      {content}
    </div>
  );
}

export default NodeIcon;
